import apiService from './apiService';

/**
 * Rückruf-Tickets of the phone agent (contract rueckruf-ticket 2026-10-01 §5):
 * a caller who could not be put through (or asked for a callback) left a wish
 * for the salon's team. Open ones show under „Wartet auf Rückmeldung“ in
 * Verläufe and in the bell until somebody marks them „Erledigt“.
 */

export type CallbackTicketTrigger = 'transfer_failed' | 'transfer_unavailable' | 'caller_request';

export type CallbackTicketStatus = 'open' | 'done';

export interface CallbackTicket {
  id: number;
  status: CallbackTicketStatus;
  trigger: CallbackTicketTrigger | string;
  /** Digits only, like every customer phone of the API (display with a leading „+“). */
  customer_phone: string;
  customer_name: string | null;
  /** Where the name came from (`profile`, `spoken`, …); null without a name. */
  name_source: string | null;
  reason: string;
  preferred_window: string | null;
  /** What the agent got stuck on. */
  stuck_note: string | null;
  agent_number: string | null;
  site_cd: string | null;
  site_name: string | null;
  call_sid: string;
  /** The stored call (`?c=`); null until the call record arrived — then `?call=<call_sid>`. */
  conversation_id: number | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  resolve_note: string | null;
  email_sent: boolean;
}

export interface CallbackTicketList {
  items: CallbackTicket[];
  open_count: number;
}

/** Longest note the server keeps when a ticket is marked done. */
export const RESOLVE_NOTE_MAX = 500;

class CallbackTicketService {
  async list(status: 'open' | 'done' | 'all' = 'open', limit = 50): Promise<CallbackTicketList> {
    const params = new URLSearchParams({ status, limit: String(limit) });
    return apiService.get<CallbackTicketList>(`/callback-tickets?${params.toString()}`);
  }

  /** Marks the ticket done (idempotent); an empty note is not sent. */
  async resolve(id: number, note?: string | null): Promise<CallbackTicket> {
    const text = note?.trim();
    const response = await apiService.post<{ ticket: CallbackTicket }>(
      `/callback-tickets/${encodeURIComponent(String(id))}/resolve`,
      text ? { note: text.slice(0, RESOLVE_NOTE_MAX) } : {},
    );
    if (!response?.ticket) throw new Error('Der Server hat das Ticket nicht zurückgegeben.');
    return response.ticket;
  }
}

export const callbackTicketService = new CallbackTicketService();
export default callbackTicketService;
